import { evidenceFilename, type EvidenceBody, type EvidenceStore } from "../store-contract";
import { getAllRows, getRow, runWrite, txDone } from "./db";

// Staged evidence bytes as Blob rows keyed by client_attachment_id — the
// browser twin of the device's per-assignment staging directory tree. The
// locator this store mints IS the clientAttachmentId: a Blob row needs no
// URI-reconstruction address, and the device's fixed staging filename is a
// filesystem detail local to that adapter, never a wire artifact. Rows are
// deleted only after the server confirms the attachment set complete (the
// engine's cleanup step); `revoked` submissions keep their staged bytes.

interface EvidenceRow {
  client_attachment_id: string;
  assignment_id: string;
  content_type: string;
  blob: Blob;
}

// Serves the arms an IndexedDB store can: a Blob re-wrapped under the
// declared content type, a data URL decoded to one. The `uri` arm REJECTS
// naming itself — this store cannot read a filesystem address, and a silent
// success would stage nothing while looking staged.
function bodyToBlob(body: EvidenceBody, contentType: string): Blob {
  switch (body.kind) {
    case "blob":
      // Re-wrapped so the stored type is the declared contentType even when
      // the caller's Blob was minted under a different one — the part's type
      // must agree with the contract-pinned filename extension at upload.
      return new Blob([body.blob], { type: contentType });
    case "dataUrl":
      return new Blob([dataUrlToBytes(body.dataUrl)], { type: contentType });
    case "uri":
      throw new Error("evidence_body_uri_unreadable: an IndexedDB store cannot read a uri address");
  }
}

const DATA_URL_BASE64_PREFIX = /^data:[^;,]*;base64,/;

// The signature canvas emits a `data:image/png;base64,...` data URL; the Blob
// wants raw bytes. The ArrayBuffer type argument (what the constructor
// allocates anyway) is what BlobPart requires.
function dataUrlToBytes(dataUrl: string): Uint8Array<ArrayBuffer> {
  const match = DATA_URL_BASE64_PREFIX.exec(dataUrl);
  if (!match) {
    throw new Error("expected a base64 data URL");
  }
  return base64ToBytes(dataUrl.slice(match[0].length));
}

const B64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

const B64_INDEX = new Map<string, number>([...B64_ALPHABET].map((ch, i) => [ch, i]));

// Pure-JS base64 decode, transcribed from the device adapter so signature
// bytes decode identically on every runtime without relying on a global atob.
function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const clean = base64.replace(/[\r\n]/g, "").replace(/=+$/, "");
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let buffer = 0;
  let bits = 0;
  let index = 0;
  for (const ch of clean) {
    const value = B64_INDEX.get(ch);
    if (value === undefined) {
      throw new Error("invalid base64 input");
    }
    buffer = (buffer << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[index] = (buffer >> bits) & 0xff;
      index += 1;
    }
  }
  return out;
}

export function createEvidenceStore(db: IDBDatabase): EvidenceStore {
  const readStore = (): IDBObjectStore =>
    db.transaction("evidence", "readonly").objectStore("evidence");

  return {
    // put is the INSERT OR REPLACE twin: re-staging the same attachment id
    // replaces its bytes rather than duplicating the row. A quota abort maps
    // to FieldSyncQuotaError("stage-evidence") — loud, blocking, non-lossy:
    // nothing was written and the capture is retriable after space is freed.
    async stageEvidence(input) {
      const blob = bodyToBlob(input.body, input.contentType);
      await runWrite(db, ["evidence"], "stage-evidence", (tx) => {
        tx.objectStore("evidence").put({
          client_attachment_id: input.clientAttachmentId,
          assignment_id: input.assignmentId,
          content_type: input.contentType,
          blob,
        } satisfies EvidenceRow);
        return Promise.resolve();
      });
      return {
        clientAttachmentId: input.clientAttachmentId,
        locator: input.clientAttachmentId,
        contentType: input.contentType,
      };
    },

    // The filename comes from the contract's extension map, never an
    // adapter-local guess — it reaches a multipart header on both platforms.
    // A missing row REJECTS: a queued regulatory submission whose staged
    // bytes have vanished is a loud failure, never a silently empty upload.
    async readEvidencePart(row) {
      const stored = await getRow<EvidenceRow>(readStore(), row.locator);
      if (stored === null) {
        throw new Error(`evidence_missing:${row.clientAttachmentId}`);
      }
      return {
        part: stored.blob,
        filename: evidenceFilename(row.clientAttachmentId, row.contentType),
      };
    },

    // Scoped to one assignment via the assignment_id index. Called only by
    // the engine's cleanup step, after the server confirms the attachment set
    // complete — never for `revoked` rows.
    async deleteEvidenceForAssignment(assignmentId) {
      const tx = db.transaction("evidence", "readwrite");
      const store = tx.objectStore("evidence");
      const rows = await getAllRows<EvidenceRow>(store.index("assignment_id"), assignmentId);
      for (const r of rows) {
        store.delete(r.client_attachment_id);
      }
      await txDone(tx);
    },
  };
}
